import { Transition, TransitionChild } from '@headlessui/react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { t } from 'i18next';
import { useState } from 'react';

export type AppNotificationType = 'error' | 'warning' | 'success' | 'notice';

export type AppNotificationProps = {
  title: string;
  message?: string | null;
  type?: AppNotificationType | null;
  onDismiss: () => void;
};

export function AppNotification({ title, message, type, onDismiss }: AppNotificationProps) {
  const [show, setShow] = useState<boolean>(true);

  return (
    <Transition show={show} appear afterLeave={onDismiss}>
      <TransitionChild
        enter="transform ease-out duration-300 transition"
        enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
        enterTo="translate-y-0 opacity-100 sm:translate-x-0"
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg border border-spark-border-subtle bg-spark-surface shadow-lg">
          <div className="p-4">
            <div className="flex items-start">
              <div className="shrink-0">
                {type === 'success' && (
                  <CheckCircleIcon className="size-6 text-spark-success-solid" aria-hidden="true" />
                )}
                {type === 'warning' && (
                  <ExclamationCircleIcon
                    className="size-6 text-spark-warning-solid"
                    aria-hidden="true"
                  />
                )}
                {type === 'error' && (
                  <ExclamationCircleIcon
                    className="size-6 text-spark-error-solid"
                    aria-hidden="true"
                  />
                )}
                {type !== 'success' && type !== 'warning' && type !== 'error' && (
                  <InformationCircleIcon
                    className="size-6 text-spark-info-solid"
                    aria-hidden="true"
                  />
                )}
              </div>

              <div className="ms-3 w-0 flex-1 pt-0.5">
                <p className="font-medium text-sm text-spark-text-primary">{title}</p>
                {message ? (
                  <p className="mt-1 text-sm text-spark-text-secondary">{message}</p>
                ) : null}
              </div>

              <div className="ms-4 flex shrink-0">
                <button
                  type="button"
                  className="inline-flex cursor-pointer rounded-md text-spark-text-tertiary hover:text-spark-text-secondary focus:outline-hidden focus:ring-2 focus:ring-spark-primary-accent focus:ring-offset-2 focus:ring-offset-spark-surface"
                  onClick={() => setShow(false)}
                >
                  <span className="sr-only">{t('aria:close')}</span>
                  <XMarkIcon className="size-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </TransitionChild>
    </Transition>
  );
}
